import { Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { EducationStatus } from '@novaheal/types';
import { CalendarIcon, CloseIcon, EducationIcon } from 'assets/icons/v2';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { SelectField } from 'components/common/Form';
import { Form, RHFCheckboxField } from 'components/common/Form/v2';
import { FormInput } from 'components/ui/form/Input';
import { NovaIcon } from 'components/ui/novaIcon';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import {
  EducationProgressionAdjustmentDto,
  EducationProgressionPromptDto,
} from 'hooks/api/backend/user/education-progression';
import Joi from 'joi';
import { ChangeEvent, ReactElement, useMemo, useRef, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { ModalEducationProgressionVariantProps, SharedModalProps } from 'types/Modal';
import {
  EDUCATION_STATUS_OPTIONS,
  EXAMINATION_MONTH_OPTIONS,
  EXAMINATION_STATUSES,
  examinationDateUnknownSchema,
  examinationMonthSchema,
  examinationYearSchema,
  getPrefilledExaminationDateKey,
  validatePrefilledExaminationDateFields,
  triggerExaminationDateFields,
  revalidateExaminationAfterStatusChange,
  pickSelectFieldRegistration,
} from 'utils/education';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

type Props = SharedModalProps & ModalEducationProgressionVariantProps;

const LEHRDRITTEL_OPTIONS = EDUCATION_STATUS_OPTIONS.filter((option) =>
  EXAMINATION_STATUSES.includes(option.value as (typeof EXAMINATION_STATUSES)[number])
);

const schema = Joi.object({
  educationStatus: Joi.string().required().messages({
    'string.empty': errorMessage.empty,
  }),
  examinationDateUnknown: examinationDateUnknownSchema,
  examinationMonth: examinationMonthSchema,
  examinationYear: examinationYearSchema,
}).required();

type EducationProgressionFormInputs = {
  educationStatus: EducationStatus;
  examinationDateUnknown: boolean;
  examinationMonth: number | null;
  examinationYear: number | null;
};

const shouldShowExaminationFields = (educationStatus: string): boolean =>
  EXAMINATION_STATUSES.includes(educationStatus as (typeof EXAMINATION_STATUSES)[number]);

const getDefaultValues = (
  prompt: EducationProgressionPromptDto
): EducationProgressionFormInputs => ({
  educationStatus: prompt.suggestedStage,
  examinationDateUnknown: prompt.currentExaminationDateUnknown ?? false,
  examinationMonth: prompt.currentExaminationDateUnknown
    ? null
    : (prompt.currentExaminationDate?.month ?? null),
  examinationYear: prompt.currentExaminationDateUnknown
    ? null
    : (prompt.currentExaminationDate?.year ?? null),
});

const toAdjustmentPayload = (
  promptId: string,
  values: EducationProgressionFormInputs
): EducationProgressionAdjustmentDto => {
  const showExamination = shouldShowExaminationFields(values.educationStatus);
  const isUnknown = showExamination && values.examinationDateUnknown;

  return {
    promptId,
    educationStatus: values.educationStatus,
    examinationDateUnknown: isUnknown,
    examination:
      showExamination && !isUnknown && values.examinationMonth && values.examinationYear
        ? { month: values.examinationMonth, year: values.examinationYear }
        : null,
  };
};

const normalizeExaminationField = (value: number | string | null | undefined): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const hasFormDataChanged = (
  values: EducationProgressionFormInputs,
  baseline: EducationProgressionFormInputs
): boolean => {
  if (values.educationStatus !== baseline.educationStatus) {
    return true;
  }

  if (!shouldShowExaminationFields(values.educationStatus)) {
    return false;
  }

  if (values.examinationDateUnknown !== baseline.examinationDateUnknown) {
    return true;
  }

  if (values.examinationDateUnknown) {
    return false;
  }

  return (
    normalizeExaminationField(values.examinationMonth) !==
      normalizeExaminationField(baseline.examinationMonth) ||
    normalizeExaminationField(values.examinationYear) !==
      normalizeExaminationField(baseline.examinationYear)
  );
};

export const EducationProgressionModal = ({
  handleClose,
  isShown,
  educationProgressionPrompt,
  onConfirm,
  onAdjust,
  onDismiss,
  isSubmitting = false,
}: Props): ReactElement => {
  const copy = useMemo(
    () => getEducationProgressionModalCopy(educationProgressionPrompt),
    [educationProgressionPrompt]
  );

  const defaultValues = useMemo(
    () => getDefaultValues(educationProgressionPrompt),
    [educationProgressionPrompt]
  );

  const isFormDirtyRef = useRef(false);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);

  const isLoading = isSubmitting || isSubmittingLocal;

  const handleDismiss = async (): Promise<void> => {
    if (isSubmitting || isSubmittingLocal) return;

    setIsSubmittingLocal(true);
    try {
      await onDismiss();
      handleClose();
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  const handleSubmit = async (values: EducationProgressionFormInputs): Promise<void> => {
    if (isSubmitting || isSubmittingLocal) return;

    setIsSubmittingLocal(true);
    try {
      const shouldAdjust = isFormDirtyRef.current || hasFormDataChanged(values, defaultValues);

      if (shouldAdjust) {
        await onAdjust(toAdjustmentPayload(educationProgressionPrompt.id, values));
      } else {
        await onConfirm();
      }
      handleClose();
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  return (
    <CustomModal isOpen={isShown} onClose={handleDismiss} placement="center" hideHeader size="lg">
      <CustomModalContent p={{ base: 'l', md: '3xl' }} maxW="740px">
        <Form<EducationProgressionFormInputs>
          schema={schema}
          onSubmit={handleSubmit}
          options={{ defaultValues, mode: 'onChange' }}
          onMount={({ trigger }) => validatePrefilledExaminationDateFields(defaultValues, trigger)}
          onMountKey={getPrefilledExaminationDateKey(defaultValues)}
        >
          {({ register, watch, setValue, formState, trigger }) => {
            isFormDirtyRef.current = formState.isDirty;

            const educationStatus = watch('educationStatus');
            const showExamination = shouldShowExaminationFields(educationStatus);
            const isExaminationUnknown = !!watch('examinationDateUnknown');
            const examinationYearRegistration = register('examinationYear', {
              valueAsNumber: true,
            });

            const clearExaminationFields = (): void => {
              setValue('examinationMonth', null, { shouldValidate: false });
              setValue('examinationYear', null, { shouldValidate: false });
            };

            const examinationErrorMessage =
              formState.errors.examinationYear?.type === 'custom'
                ? formState.errors.examinationYear.message
                : formState.errors.examinationMonth?.message ||
                  formState.errors.examinationYear?.message;

            return (
              <Flex direction="column" gap="4xl">
                <Flex direction="column" gap="l">
                  <Flex direction="column" align="flex-end" gap="2xs" w="full">
                    <NovaIconButton
                      aria-label="Schließen"
                      variant="primary"
                      size="l"
                      onClick={handleDismiss}
                      icon={CloseIcon}
                      iconProps={{ color: 'text.primary' }}
                      disabled={isLoading}
                    />
                    <VStack gap="s" align="flex-start" w="full">
                      <Text textStyle="h4.medium" color="text.primary">
                        {copy.title}
                      </Text>
                      <Text textStyle="body.m.regular" color="text.secondary">
                        {copy.description}
                      </Text>
                      <Text textStyle="body.xs.regular" color="text.tertiary">
                        {copy.helper}
                      </Text>
                    </VStack>
                  </Flex>

                  <VStack align="stretch" gap="s" w="full">
                    <SelectField
                      options={LEHRDRITTEL_OPTIONS}
                      placeholder="Bitte auswählen"
                      registration={pickSelectFieldRegistration(register('educationStatus'))}
                      leftElement={
                        <NovaIcon
                          icon={EducationIcon}
                          size="s"
                          color="icon.quaternary"
                          aria-hidden
                        />
                      }
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        revalidateExaminationAfterStatusChange(e.target.value as EducationStatus, {
                          setValue,
                          trigger,
                          shouldShowExaminationFields,
                          clearExaminationFields,
                        }).catch(() => undefined);
                      }}
                    />

                    {showExamination && (
                      <VStack align="stretch" gap="s" w="full">
                        <VStack align="stretch" gap="2xs">
                          <Text textStyle="body.m.regular" color="text.secondary">
                            {copy.examinationQuestion}
                          </Text>
                          {copy.examinationHint && (
                            <Text textStyle="body.xs.regular" color="text.tertiary">
                              {copy.examinationHint}
                            </Text>
                          )}
                        </VStack>

                        <VStack align="stretch" gap="xs" w="full">
                          <HStack
                            key={isExaminationUnknown ? 'exam-unknown' : 'exam-known'}
                            w="full"
                            gap="l"
                            alignItems="flex-start"
                          >
                            <SelectField
                              options={EXAMINATION_MONTH_OPTIONS}
                              placeholder="Monat"
                              registration={register('examinationMonth')}
                              disabled={isExaminationUnknown}
                              leftElement={
                                <NovaIcon
                                  icon={CalendarIcon}
                                  size="s"
                                  color="icon.quaternary"
                                  aria-hidden
                                />
                              }
                              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                setValue('examinationMonth', Number(e.target.value), {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: false,
                                });
                                triggerExaminationDateFields(trigger).catch(() => undefined);
                              }}
                            />
                            <FormInput
                              id="examinationYear"
                              placeholder="Jahr"
                              disabled={isExaminationUnknown}
                              error={{ type: 'suppressed', message: undefined } as FieldError}
                              leftElement={
                                <NovaIcon
                                  icon={CalendarIcon}
                                  size="s"
                                  color="icon.quaternary"
                                  aria-hidden
                                />
                              }
                              registration={{
                                ...examinationYearRegistration,
                                onChange: async (event) => {
                                  await examinationYearRegistration.onChange(event);
                                  await triggerExaminationDateFields(trigger).catch(
                                    () => undefined
                                  );
                                },
                              }}
                            />
                          </HStack>
                          {examinationErrorMessage && (
                            <Text w="full" textStyle="body.xs.regular" color="text.error">
                              {String(examinationErrorMessage)}
                            </Text>
                          )}
                        </VStack>

                        <RHFCheckboxField<EducationProgressionFormInputs>
                          name="examinationDateUnknown"
                          onChange={(checked) => {
                            if (checked) {
                              clearExaminationFields();
                            }
                            triggerExaminationDateFields(trigger).catch(() => undefined);
                          }}
                          labelElement={
                            <Text textStyle="body.s.regular">
                              Ich kenne meinen Termin noch nicht – ich ergänze ihn später.
                            </Text>
                          }
                        />
                      </VStack>
                    )}
                  </VStack>
                </Flex>

                <Flex gap="s" justify="flex-end">
                  <Button
                    type="button"
                    onClick={handleDismiss}
                    variant="tertiary"
                    disabled={isLoading}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                    Weiter
                  </Button>
                </Flex>
              </Flex>
            );
          }}
        </Form>
      </CustomModalContent>
    </CustomModal>
  );
};

type EducationProgressionModalCopy = {
  title: string;
  description: string;
  helper: string;
  examinationQuestion: string;
  examinationHint: string | null;
};

const PROFILE_HELPER =
  'Deine Angaben helfen uns, dir die richtigen Inhalte und Erinnerungen zur richtigen Zeit zu zeigen.';

const EXAMINATION_HINT_UNKNOWN =
  'Das ist meistens die schriftliche Prüfung. Wenn du es nicht weißt, frag deine Pflegeschule.';

/** Profile snapshot from the prompt — not live form values. */
const hadKnownExaminationDateInProfile = (prompt: EducationProgressionPromptDto): boolean =>
  prompt.currentExaminationDateUnknown === false;

const getExaminationFieldCopy = (
  prompt: EducationProgressionPromptDto
): Pick<EducationProgressionModalCopy, 'examinationQuestion' | 'examinationHint'> => {
  if (hadKnownExaminationDateInProfile(prompt)) {
    return {
      examinationQuestion: 'Beginn deiner Abschlussprüfungen',
      examinationHint: null,
    };
  }

  return {
    examinationQuestion: 'Wann beginnen deine Abschlussprüfungen?',
    examinationHint: EXAMINATION_HINT_UNKNOWN,
  };
};

const getEducationProgressionModalCopy = (
  prompt: EducationProgressionPromptDto
): EducationProgressionModalCopy => {
  const examinationCopy = getExaminationFieldCopy(prompt);
  const hasKnownExamDate = hadKnownExaminationDateInProfile(prompt);
  const isFirstToSecond = prompt.currentStage === 'FIRST' && prompt.suggestedStage === 'SECOND';
  const isSecondToLast = prompt.currentStage === 'SECOND' && prompt.suggestedStage === 'LAST';
  const getEduThirdNumber = (suggestedStage: EducationStatus): number => {
    switch (suggestedStage) {
      case 'FIRST':
        return 1;
      case 'SECOND':
        return 2;
      case 'LAST':
        return 3;
      default:
        return 3;
    }
  };

  if (prompt.isFollowUp) {
    return {
      title: 'Kurze Erinnerung zu deinen Ausbildungsdaten',
      description: hasKnownExamDate
        ? 'Bitte prüfe kurz, ob dein Lehrdrittel und deine Abschlussprüfungen noch stimmen.'
        : 'Bitte prüfe kurz, ob dein Lehrdrittel noch stimmt.',
      helper: PROFILE_HELPER,
      ...examinationCopy,
    };
  }

  if (isFirstToSecond) {
    return {
      title: 'Herzlichen Glückwunsch – das erste Jahr ist geschafft! 🎉',
      description: hasKnownExamDate
        ? 'Wahrscheinlich bist du jetzt im 2. Lehrdrittel. Prüfe kurz, ob deine Angaben noch stimmen.'
        : 'Wahrscheinlich bist du jetzt im 2. Lehrdrittel. Prüfe kurz deine Angaben.',
      helper: PROFILE_HELPER,
      ...examinationCopy,
    };
  }

  if (isSecondToLast) {
    return {
      title: 'Stark, wie weit du schon gekommen bist! 🎉',
      description:
        'Du hast schon viel gelernt und viel geleistet. Wahrscheinlich bist du jetzt im 3. Lehrdrittel. Prüfe kurz, ob deine Angaben noch stimmen.',
      helper: PROFILE_HELPER,
      ...examinationCopy,
    };
  }

  return {
    title: 'Stark, wie weit du schon gekommen bist! 🎉',
    description: `Du hast schon viel gelernt und viel geleistet. Wahrscheinlich bist du jetzt im ${getEduThirdNumber(prompt.suggestedStage)}. Lehrdrittel. Prüfe kurz, ob deine Angaben noch stimmen.`,
    helper: PROFILE_HELPER,
    ...examinationCopy,
  };
};
